import React, { useEffect, useRef, useState } from 'react';
import ErrorDialog from 'components/ErrorDialog';
import { ERRORS } from '../../../../../constants';
import LrGrid from 'components/LrGrid/components/Grid';
import LumenLoader from 'components/Loader';
import { columnDefs } from './GridDefs';
import {
    deleteClusterJob,
    fetchLocationClusterData,
    getClusterStatus,
    startLocationClusterOperation,
} from 'services/reporting';
import { GridReadyEvent } from 'ag-grid-enterprise';
import { LocationClusterResource, ReportingTabProps } from '../../../../../types/reporting.type';
import { Grid, MenuItem } from '@mui/material';
import { Page } from 'components/Layout';
import { Select } from 'components/Input';
import Spacing from 'components/Layout/components/Spacing';
import { GridApi } from 'ag-grid-enterprise';
import ClusteredLocation from 'application/Models/ClusteredLocation';

const LocationCluster: React.FunctionComponent<ReportingTabProps> = (props) => {
    const { reportName } = props;
    const [locationClusterData, setLocationClusterData] = useState<LocationClusterResource[]>([]);
    const [flattenedClusterData, setFlattenedClusterData] = useState<
        (ClusteredLocation & { hierarchyData?: string[] })[]
    >([]);
    const [error, setError] = useState<boolean>();
    const [selectedRadius, setSelectedRadius] = useState('1');
    const [gridReady, setGridReady] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>();
    const [isLoading, setIsLoading] = useState(false);
    const currentJobId = useRef<string>('');
    const isLoadingRef = useRef<boolean>(isLoading);
    const checkTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    isLoadingRef.current = isLoading;

    const checkJobComplete = (jobId: string) => {
        checkTimeoutRef.current = setTimeout(async () => {
            try {
                const status = await getClusterStatus(jobId);
                if (status === 'COMPLETE') {
                    const response = await fetchLocationClusterData(jobId);
                    setLocationClusterData(response);
                    setIsLoading(false);
                    currentJobId.current = '';
                    await deleteClusterJob(jobId);
                } else if (isLoadingRef.current) {
                    checkJobComplete(jobId);
                }
            } catch (e) {
                setError(true);
                setIsLoading(false);
            }
        }, 5000);
    };

    const startClusterOperation = async (radius: string) => {
        if (gridReady) {
            try {
                setIsLoading(true);
                const jobId = await startLocationClusterOperation(radius);
                currentJobId.current = jobId.jobId;
                checkJobComplete(jobId.jobId);
            } catch (e) {
                setError(true);
                setIsLoading(false);
                if (currentJobId.current) {
                    deleteClusterJob(currentJobId.current);
                }
                currentJobId.current = '';
            }
        }
    };

    const onGridReady = (params: GridReadyEvent) => {
        setGridApi(params.api);
        setGridReady(true);
    };

    useEffect(() => {
        if (isLoading) {
            gridApi?.showLoadingOverlay();
        } else {
            gridApi?.hideOverlay();
        }
    }, [isLoading]);

    useEffect(() => {
        const flattened: (ClusteredLocation & { hierarchyData?: string[] })[] = [];
        locationClusterData.forEach((lc) => {
            lc.clusterLocations.forEach((cl) => {
                const cur: ClusteredLocation & { hierarchyData?: string[] } = { ...cl };
                cur.hierarchyData = [lc.clusterId, cl.name.toString()];
                flattened.push(cur);
            });
        });
        setFlattenedClusterData(flattened);
    }, [locationClusterData]);

    useEffect(() => {
        startClusterOperation(selectedRadius);
    }, [selectedRadius, gridReady]);

    useEffect(() => {
        return () => {
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }
            if (currentJobId.current) {
                deleteClusterJob(currentJobId.current);
            }
        };
    }, []);

    const handleClusterRadiusChange = (event: any) => {
        const newSelectedRadius = event.target.value;
        setSelectedRadius(newSelectedRadius);
    };

    return (
        <>
            <Grid item xs={12}>
                <Page title={'Location Clusters'}>
                    <Grid container spacing={2}>
                        <Grid item xs={2}>
                            <Select
                                variant={'standard'}
                                id={'cluster-radius-select'}
                                label={'Cluster Radius'}
                                onChange={handleClusterRadiusChange}
                                disabled={isLoading}
                                value={selectedRadius}
                            >
                                <MenuItem value={'1'}>1 Mile</MenuItem>
                                <MenuItem value={'2.5'}>2.5 Miles</MenuItem>
                                <MenuItem value={'5'}>5 Miles</MenuItem>
                                <MenuItem value={'10'}>10 Miles</MenuItem>
                            </Select>
                        </Grid>
                        <Grid item xs={10} container direction={'column'} spacing={2} alignItems={'flex-start'}>
                            <Grid item style={{ width: '100%' }}>
                                {/* LEGACY: Do not use this pattern. Use the error context (useErrorDialog) instead. */}
                                {/* See examples in `src/views/SubmissionDetails/index.tsx` and `src/views/SubmissionDetails/components/QuotesBind/index.tsx` */}
                                {error && (
                                    <ErrorDialog
                                        errorTitle={ERRORS.API_ERROR_MSG_TITLE}
                                        errorMsg={ERRORS.API_ERROR_MSG_BODY}
                                    />
                                )}
                                <LrGrid
                                    onGridReady={onGridReady}
                                    gridClassName="ag-theme-balham"
                                    components={{ lumenLoader: LumenLoader }}
                                    loadingOverlayComponent="lumenLoader"
                                    suppressColumnVirtualisation
                                    animateRows
                                    columnDefs={columnDefs}
                                    rowData={flattenedClusterData}
                                    autosize={true}
                                    treeData={true}
                                    suppressCsvExport={true}
                                    hideStatusBar
                                    // defaultExportParams={{
                                    //     processCellCallback: exportCellCallback,
                                    // }}
                                    getDataPath={(data: any) => {
                                        return data.hierarchyData;
                                    }}
                                    autoGroupColumnDef={{
                                        pinned: 'left',
                                        lockPosition: true,
                                        headerName: 'Cluster',
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Spacing height={'18px'} />
                </Page>
            </Grid>
        </>
    );
};

export default LocationCluster;
